import { useState } from 'react';
import { supabase } from '@shared/lib/supabase';
import SaveBar from './SaveBar';
import ImageCropper from './ImageCropper';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver',
  'America/Sao_Paulo', 'America/Argentina/Buenos_Aires', 'America/Mexico_City',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome',
  'Europe/Amsterdam', 'Europe/Stockholm', 'Europe/Moscow',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Singapore', 'Asia/Hong_Kong',
  'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland',
  'Africa/Lagos', 'Africa/Cairo', 'Africa/Johannesburg',
];

interface ProfileTabProps {
  userId: string;
  businessName: string;
  setBusinessName: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  email: string;
  avatarUrl: string;
  setAvatarUrl: (v: string) => void;
  timezone: string;
  setTimezone: (v: string) => void;
  saving: boolean;
  saveSuccess: boolean;
  saveError: string;
  onSave: () => void;
}

export default function ProfileTab({
  userId,
  businessName,
  setBusinessName,
  phone,
  setPhone,
  email,
  avatarUrl,
  setAvatarUrl,
  timezone,
  setTimezone,
  saving,
  saveSuccess,
  saveError,
  onSave,
}: ProfileTabProps) {
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [uploadHover, setUploadHover] = useState(false);
  const [removeHover, setRemoveHover] = useState(false);

  const inputClass = 'w-full rounded px-3 py-2 font-sans text-sm focus:outline-none transition-colors';
  const labelClass = 'block font-mono text-[10px] uppercase tracking-widest mb-1.5';

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--dash-border)',
    background: 'var(--dash-input)',
    color: 'var(--dash-text-secondary)',
  };

  const inputFocusStyle: React.CSSProperties = {
    ...inputStyle,
    borderColor: 'var(--dash-active-border)',
  };

  const labelStyle: React.CSSProperties = {
    color: 'var(--dash-text-muted)',
  };

  const [focusedField, setFocusedField] = useState<string | null>(null);

  const getInputStyle = (field: string) =>
    focusedField === field ? inputFocusStyle : inputStyle;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file (JPG, PNG)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be under 5MB');
      return;
    }
    setUploadError('');
    setCropFile(file);
    e.target.value = '';
  };

  const handleCrop = async (blob: Blob) => {
    setCropFile(null);
    setUploadingAvatar(true);
    setUploadError('');

    const filePath = `${userId}/avatar.png`;
    const { error: uploadErr } = await supabase.storage
      .from('avatars')
      .upload(filePath, blob, { upsert: true, contentType: 'image/png' });

    if (uploadErr) {
      setUploadError(uploadErr.message);
      setUploadingAvatar(false);
      return;
    }

    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
    if (urlData?.publicUrl) {
      setAvatarUrl(`${urlData.publicUrl}?t=${Date.now()}`);
    }
    setUploadingAvatar(false);
  };

  const handleRemoveAvatar = async () => {
    if (!avatarUrl) return;
    setUploadingAvatar(true);
    const filePath = `${userId}/avatar.png`;
    await supabase.storage.from('avatars').remove([filePath]);
    setAvatarUrl('');
    setUploadingAvatar(false);
  };

  return (
    <>
      {cropFile && (
        <ImageCropper
          imageFile={cropFile}
          onCrop={handleCrop}
          onCancel={() => setCropFile(null)}
        />
      )}

      <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
        <h2 className="font-mono text-[10px] uppercase tracking-widest mb-5" style={{ color: 'var(--dash-text-muted)' }}>Profile</h2>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div
              className="relative w-16 h-16 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
              style={{ background: 'var(--dash-avatar-bg)', border: '1px solid var(--dash-border)' }}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-lg" style={{ color: 'var(--dash-text-faint)' }}>&#9675;</span>
              )}
              {uploadingAvatar && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="inline-block animate-spin text-white text-sm">&#9676;</span>
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <label
                  className="inline-flex items-center gap-2 cursor-pointer font-mono text-xs transition-colors rounded px-3 py-1.5"
                  style={{
                    color: uploadHover ? 'var(--dash-text-secondary)' : 'var(--dash-text-muted)',
                    border: '1px solid var(--dash-border)',
                  }}
                  onMouseEnter={() => setUploadHover(true)}
                  onMouseLeave={() => setUploadHover(false)}
                >
                  <span className="text-[11px]">&uarr;</span>
                  Upload Photo
                  <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileSelect} className="hidden" />
                </label>
                {avatarUrl && (
                  <button
                    onClick={handleRemoveAvatar}
                    className="font-mono text-xs transition-colors rounded px-3 py-1.5"
                    style={{
                      color: removeHover ? 'var(--color-red-400, #f87171)' : 'var(--dash-text-faint)',
                      border: '1px solid var(--dash-border)',
                    }}
                    onMouseEnter={() => setRemoveHover(true)}
                    onMouseLeave={() => setRemoveHover(false)}
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className="font-sans text-[10px] mt-1" style={{ color: 'var(--dash-text-faint)' }}>JPG, PNG, WebP. You can resize and adjust after selecting.</p>
              {uploadError && <p className="font-sans text-[10px] text-red-400 mt-1">{uploadError}</p>}
            </div>
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Business Name</label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Your business name"
              className={inputClass}
              style={getInputStyle('businessName')}
              onFocus={() => setFocusedField('businessName')}
              onBlur={() => setFocusedField(null)}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Email Address</label>
            <input
              type="email"
              value={email}
              disabled
              className={`${inputClass} cursor-not-allowed`}
              style={{ ...inputStyle, color: 'var(--dash-text-muted)' }}
            />
            <p className="font-sans text-[10px] mt-1" style={{ color: 'var(--dash-text-faint)' }}>Email is set during registration</p>
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Phone Number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (555) 000-0000"
              className={inputClass}
              style={getInputStyle('phone')}
              onFocus={() => setFocusedField('phone')}
              onBlur={() => setFocusedField(null)}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className={inputClass}
              style={{ ...inputStyle, color: 'var(--dash-select-text)' }}
            >
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <p className="font-sans text-[10px] mt-1" style={{ color: 'var(--dash-text-faint)' }}>Used for date and time display throughout the dashboard</p>
          </div>
          <SaveBar saving={saving} saveSuccess={saveSuccess} saveError={saveError} onSave={onSave} />
        </div>
      </div>
    </>
  );
}
